import { createContext, useContext, useEffect, useState, useRef, ReactNode, useCallback, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { getAdminAuthStatus } from '@/lib/adminAuthStatus';
import { supabase } from '@/integrations/supabase/client';
import { toast, clockSkewToast } from "@/lib/toast";
import { useNavigate } from 'react-router-dom';
import { queryClient, clearAuthReplayGuard } from '@/lib/queryClient';
import { resetAuthRecovery } from '@/lib/authErrorRecovery';
import { setUser as setSentryUser } from '@/lib/sentry';
import { clearSessionToken, SessionInvalidReason } from '@/hooks/useSessionValidation';
import { revokeSessionForLogout, leaveToPublicRoute } from '@/lib/sessionLogout';
import { clearStoredBookingContact } from '@/utils/bookingContactStorage';
import { alignProfileLanguage } from '@/lib/auth/alignProfileLanguage';
import { useLanguage } from '@/contexts/LanguageContext';
import { markSessionExpected, markSessionAbsent, markLogoutStarted, markLogoutFinished } from '@/lib/auth/sessionExpectation';
import { tabOwnsAuthEvent, readAuthStorageKey } from '@/lib/auth/tabOwnsAuthEvent';
import { hybridAuthStorage } from '@/integrations/supabase/authStorage';
import { isSessionStaleOnArrival, markClockSkewDetected, clearClockSkew } from '@/lib/auth/clockSkew';
import { registerSessionRefresher } from '@/lib/auth/sessionGuardedFetch';
import { refreshSessionForGuard } from '@/lib/auth/sessionRefresher';
import { startNativeAutoRefresh } from '@/lib/auth/nativeAutoRefresh';
import { hasExceededInactivityWindow } from '@/lib/auth/nativeInactivity';
import { clearNativeAuthStore } from '@/integrations/supabase/nativeAuthStore';
import { resetAnnouncementOpenState } from '@/lib/announcementOpenState';
import { shouldFollowCrossTabSignOut } from '@/lib/auth/crossTabSignOut';

// La porte est construite dans `client.ts`, qui ne peut pas importer le
// renouvellement sans créer un cycle. On le lui donne ici, au chargement du
// module : `AuthContext` est monté une fois, au démarrage, avant toute requête.
registerSessionRefresher(refreshSessionForGuard);

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  adminRequires2FA: boolean;
  isOwner: boolean;
  isEmployee: boolean;
  isClient: boolean;
  checkingRole: boolean;
  isSigningOut: boolean;
  initialAuthDone: boolean;
  setAdminState: (isAdmin: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Exported flag to allow auth pages to skip role check during 2FA flow
// AdminAuth resolves the signed session status itself during password submission.
export const skipRoleCheckRef = { current: false };

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  // La personne que CET onglet affiche, pour le canal de déconnexion
  // `flowcut-auth`. Recopiée de l'état `user`, donc juste quel que soit le
  // chemin qui l'a posé (démarrage, événement, déconnexion).
  // ⛔ Pas `currentUserIdRef` : il n'est pas renseigné au démarrage (seul le
  // rappel d'auth l'écrit), un onglet rechargé s'y croirait « personne ».
  const shownUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    shownUserIdRef.current = user?.id ?? null;
  }, [user]);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRequires2FA, setAdminRequires2FA] = useState(false);
  const adminMembershipRef = useRef(false);
  const authVersionRef = useRef(0);
  const [isOwner, setIsOwner] = useState(false);
  const [isEmployee, setIsEmployee] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [checkingRole, setCheckingRole] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [initialAuthDone, setInitialAuthDone] = useState(false);
  const isSigningOutRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);
  const roleCheckInProgressRef = useRef(false);
  const lastCheckedUserIdRef = useRef<string | null>(null);
  const roleCheckPromiseRef = useRef<Promise<void> | null>(null);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  // Minuteurs différés du provider (contrôle de rôle après un événement d'auth,
  // remise à zéro du drapeau de déconnexion). Ils doivent tous mourir avec le
  // provider : passée cette limite, leur seul effet serait d'écrire dans un
  // arbre React démonté. Vu en CI le 2026-08-01 sous la forme de 3 erreurs non
  // rattrapées « ReferenceError: window is not defined » — le minuteur se
  // déclenchait après la destruction de l'environnement jsdom du fichier de
  // test. Verrou : `authProviderTimerCleanup.test.tsx`.
  const pendingTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  // Verrou de l'arrêt pour horloge décalée : une rafale de sessions périmées
  // (un TOKEN_REFRESHED par requête) ne doit lancer qu'UNE déconnexion.
  const clockSkewStopInFlightRef = useRef(false);
  const scheduleDeferred = useCallback((run: () => void, delayMs: number) => {
    const timer = setTimeout(() => {
      pendingTimersRef.current.delete(timer);
      run();
    }, delayMs);
    pendingTimersRef.current.add(timer);
  }, []);

  useEffect(() => {
    const pending = pendingTimersRef.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  // La langue du profil suit celle de l'écran. `handle_new_user` retombe sur
  // `'en'` quand la métadonnée `language` est absente, et `signInWithOAuth` ne
  // sait pas la transmettre : un compte créé avec Google naissait donc en
  // anglais sur un salon francophone (mesuré en production le 2026-08-18).
  // Ici plutôt que dans le tunnel, pour couvrir AUSSI la connexion depuis
  // `/auth/client`, et non seulement la réservation.
  // ⚠️ La garde `isClient` protège les profils propriétaire et employé, qui ont
  // leur propre réglage de langue.
  useEffect(() => {
    if (!user || !isClient) return;
    void alignProfileLanguage(user.id, language);
  }, [user, isClient, language]);

  // Cross-tab logout sync: one channel per AuthProvider instance.
  // When a tab triggers signOut, it posts a message so other tabs clean up
  // without waiting for the next 5-minute validation tick.
  const [broadcastChannel] = useState<BroadcastChannel | null>(() =>
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('flowcut-auth') : null
  );

  // Function to manually set admin state (used by AdminAuth after 2FA verification)
  const setAdminState = useCallback((admin: boolean) => {
    setIsAdmin(admin);
    if (admin) setAdminRequires2FA(false);
    if (admin) {
      setIsOwner(false);
      setIsEmployee(false);
      setIsClient(false);
    }
  }, []);

  useEffect(() => {
    // La clé sous laquelle auth-js range la session de CET onglet. `null` si
    // introuvable (tests qui simulent `supabase.auth`) : la garde ci-dessous
    // ne s'applique alors pas, comportement d'avant.
    const authStorageKey = readAuthStorageKey(supabase.auth);

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // ⛔ Écho d'un AUTRE onglet qui contredit le stockage de celui-ci : on
        // l'ignore entièrement. supabase-js rejoue les événements des autres
        // onglets avec LEUR session ; un onglet `/book` anonyme adoptait ainsi
        // celle du propriétaire, et la porte éjectait le client (régression du
        // 2026-09-13, trace 2026-09-25-multi-onglets-echo-session.md).
        // Lecture SYNCHRONE du stockage, sans `supabase.auth` : ce rappel tourne
        // sous le verrou exclusif du SDK.
        if (
          authStorageKey &&
          !tabOwnsAuthEvent(event, session, () => hybridAuthStorage.getItem(authStorageKey))
        ) {
          return;
        }
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          authVersionRef.current += 1;
          lastCheckedUserIdRef.current = null;
          if (event === "SIGNED_IN" && !skipRoleCheckRef.current) setCheckingRole(true);
          setIsAdmin(false);
          setAdminRequires2FA(false);
        }
        // Silently handle token refresh errors as they're expected when sessions expire
        if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          // Horloge du poste en avance. Spec du 2026-09-13, trace
          // `docs/features/auth-inscription/2026-09-13-tempete-jetons-oxyd-horloge-du-poste.md`.
          //
          // auth-js ne livre jamais une session par SIGNED_IN ou TOKEN_REFRESHED
          // dans les 90 s de son expiration sans l'avoir rafraîchie. Si elle
          // paraît DÉJÀ expirée à son arrivée, seule une horloge en avance de
          // plus de 58,5 min l'explique, et `supabase-js` va relancer un
          // rafraîchissement à CHAQUE requête jusqu'au 429.
          //
          // ⛔ `supabase-js` rejoue dans chaque onglet les événements des
          // AUTRES onglets (BroadcastChannel, `GoTrueClient.ts:394-398`). Un
          // onglet gelé plus de 58,5 min (portable en veille) peut livrer à son
          // réveil un vieux `TOKEN_REFRESHED` dont la session est réellement
          // expirée, avec une horloge JUSTE. Dans le doute, on ne déconnecte
          // pas : l'étape différée reconfirme sur la session COURANTE via
          // `getSession()` avant d'agir, puisque auth-js ne garde jamais en
          // stockage une session qu'il sait expirée sans la rafraîchir.
          // ⛔ Aucun appel ATTENDU à `supabase.auth` ICI : un `await` dans ce
          // rappel tournerait sous le verrou exclusif du SDK
          // (GoTrueClient.ts:2189) et bloquerait. L'arrêt est différé hors du
          // rappel pour cette raison.
          // ⛔ `scope: 'local'` : sans lui, `signOut` vaut `global` et déconnecte
          // TOUS les appareils de la personne.
          // ⛔ Jamais le `signOut` du contexte : il recharge la page sur le web,
          // et le message d'horloge serait effacé.
          // ⛔ SIGNED_OUT ne lève PAS le drapeau : l'arrêt différé en produit
          // justement un, et les faux messages d'OwnerAuth partent juste après.
          if (session && event !== 'SIGNED_OUT') {
            if (isSessionStaleOnArrival(session.expires_at, Date.now())) {
              // Synchrone : le filtre des toasts doit être armé tout de suite,
              // avant même la reconfirmation différée ci dessous.
              markClockSkewDetected(performance.now());
              if (!clockSkewStopInFlightRef.current) {
                clockSkewStopInFlightRef.current = true;
                scheduleDeferred(() => {
                  void (async () => {
                    try {
                      const { data } = await supabase.auth.getSession();
                      const current = data.session;
                      if (!current) {
                        // Le SDK a déjà effacé la session (429 de fin de
                        // tempête) : rien à déconnecter, et on garde la
                        // fenêtre armée pour que le filtre remplace les faux
                        // messages qui suivent.
                        return;
                      }
                      if (!isSessionStaleOnArrival(current.expires_at, Date.now())) {
                        // Fausse alerte : vieil événement rejoué par un autre
                        // onglet, horloge juste.
                        clearClockSkew();
                        return;
                      }
                      clockSkewToast();
                      await supabase.auth.signOut({ scope: 'local' });
                    } catch {
                      // Dans le doute, on ne fait rien de plus.
                    } finally {
                      clockSkewStopInFlightRef.current = false;
                    }
                  })();
                }, 0);
              }
            } else {
              clearClockSkew();
            }
          }

          setSession(session);
          setUser(session?.user ?? null);

          // Le drapeau que lit la porte (`sessionGuardedFetch.ts`). Il suit la
          // SESSION, jamais l'état React `user` : c'est précisément l'écart
          // entre les deux qui laissait partir des requêtes en rôle `anon`.
          if (session) markSessionExpected();
          else markSessionAbsent();

          // Une session vivante réarme le rattrapage des 401 : sans ça, le
          // verrou « session morte » posé par un échec précédent survivrait à
          // la reconnexion et bloquerait tout rafraîchissement ultérieur.
          if (session) {
            resetAuthRecovery();
            clearAuthReplayGuard();
          }

          // Changement de compte sur le même appareil : les coordonnées que le
          // tunnel a gardées appartiennent à la personne d'AVANT. Sans cette
          // purge, la confirmation de la seconde personne part à l'adresse de
          // la première (constaté deux fois en production le 2026-08-01).
          //
          // La condition exige DEUX identifiants connus et différents. Une
          // arrivée depuis `null` n'est pas un changement de personne (c'est la
          // connexion de celle qui vient de saisir ses coordonnées), et un
          // départ vers `null` est traité par `signOut` — pas ici, car
          // `ClientAuthPanel` émet un `SIGNED_OUT` volontaire au milieu du
          // tunnel de code par courriel (barrière du 28-07).
          const previousUserId = currentUserIdRef.current;
          const nextUserId = session?.user?.id ?? null;
          if (previousUserId && nextUserId && previousUserId !== nextUserId) {
            clearStoredBookingContact();
          }

          // Update current user ref to track rapid auth state changes
          currentUserIdRef.current = nextUserId;

          // Track user in Sentry for error correlation
          if (session?.user) {
            setSentryUser(session.user.id, session.user.email);
          } else {
            setSentryUser(null);
          }

          // Check for role after session is set
          if (session?.user) {
            // Capture user ID at time of event to check if still valid when timeout executes
            const userIdAtTimeOfEvent = session.user.id;
            // Defer role check to avoid deadlocks (increased delay for better state settling)
            scheduleDeferred(() => {
              // Skip role check if explicitly requested (e.g., AdminAuth 2FA flow)
              if (skipRoleCheckRef.current) {
                return;
              }
              // Only check role if user hasn't changed (e.g., signed out for 2FA)
              if (currentUserIdRef.current === userIdAtTimeOfEvent) {
                // Keep nonadmin role caching on refresh. Admin session proof
                // can change independently of membership and must be checked again.
                if (lastCheckedUserIdRef.current === userIdAtTimeOfEvent &&
                    !adminMembershipRef.current && event !== "SIGNED_IN") {
                  return;
                }
                checkUserRole(userIdAtTimeOfEvent);
              }
            }, 50);
          } else {
            setIsAdmin(false);
            setIsOwner(false);
            setIsEmployee(false);
            setIsClient(false);
            setCheckingRole(false);
            // Si on efface les rôles, on efface aussi le souvenir de les avoir
            // calculés. Sans cette ligne, une reconnexion du MÊME utilisateur
            // est sautée par la garde `lastCheckedUserIdRef` plus haut, et les
            // rôles restent à `false` pour toute la vie de la page.
            //
            // Ce n'est pas théorique : `ClientAuthPanel` appelle
            // `supabase.auth.signOut()` en direct au milieu du tunnel `/book`
            // pour jeter la session issue du code courriel avant d'exiger le
            // mot de passe. Ce chemin ne passe donc jamais par le `signOut` du
            // contexte, seul endroit qui nettoyait ce souvenir jusqu'ici.
            lastCheckedUserIdRef.current = null;
          }
        }
      }
    );

    // Check for existing session.
    //
    // Sur natif, on vérifie D'ABORD la borne des 6 mois : au-delà, on vide le coffre
    // avant même de demander la session, pour que `getSession()` ne trouve rien et que
    // l'app démarre proprement déconnectée. Sur web, `hasExceededInactivityWindow()`
    // rend toujours false et cette étape ne coûte rien.
    hasExceededInactivityWindow()
      // Une borne qui échoue à répondre ne doit jamais bloquer le démarrage : on la
      // traite comme « pas expirée » et on laisse `getSession()` s'exécuter quand même.
      .catch(() => false)
      .then(async (expiree) => {
        if (!expiree) return;
        try {
          await supabase.auth.signOut({ scope: 'local' });
        } catch {
          // Le réseau n'est pas une condition pour se déconnecter localement.
        }
        await clearNativeAuthStore();
        // Même geste que les deux branches de `signOut` ci-dessous : sans conséquence
        // aujourd'hui puisque rien n'a encore lu le jeton de session unique à ce stade
        // du démarrage, mais laisser cette garde s'écarter de `signOut` serait une
        // incohérence qui finirait par coûter cher à quelqu'un plus tard.
        clearSessionToken();
      })
      .then(() => supabase.auth.getSession())
      .then(async ({ data: { session }, error }) => {
      if (error) {
        console.warn('Session check error:', error.message);
      }
      setSession(session);
      setUser(session?.user ?? null);
      if (session) markSessionExpected();
      else markSessionAbsent();
      if (session?.user) {
        await checkUserRole(session.user.id);
      }
      setLoading(false);
      setInitialAuthDone(true);
    }).catch((error) => {
      console.warn('Session initialization error:', error);
      setLoading(false);
      // ⛔ NE PAS retirer : cette ligne est la VOIE DE SORTIE du démarrage.
      //
      // `RedirectToMarketing` (App.tsx) fait `if (!initialAuthDone) return;` puis
      // rend `<LoadingFallback />`. Sans ce `true` ici, un rejet de la chaîne
      // ci-dessus laissait le drapeau à `false` pour toute la vie du processus :
      // un visiteur porteur d'un jeton restait sur un spinner DÉFINITIVEMENT, et
      // sur natif il n'a même pas le rechargement pour s'en sortir.
      // `ProtectedRoute.tsx` lit le même drapeau (protégé, lui, par `checkingRole`).
      //
      // Le maillon qui rejette pour de vrai est `clearNativeAuthStore()`
      // (nativeAuthStore.ts), dont le `await configure()` est hors try/catch.
      // Chemin étroit (natif, plus de 6 mois d'inactivité), conséquence totale.
      //
      // ⚠️ Le drapeau doit rester OPTIMISTE : ne jamais le repasser à `false` sur
      // le chemin nominal pour « attendre » l'utilisateur. Un retour légitime
      // arrive avec `user` encore `null` au premier rendu, et rendre l'écran de
      // confirmation à cet instant est un bug déjà vécu en production le 31-07.
      // Voir docs/features/auth-inscription/2026-08-08-book-step-review-squelette-bloque.md
      setInitialAuthDone(true);
    });

    return () => subscription.unsubscribe();
  }, [scheduleDeferred]);

  const checkUserRole = async (userId: string) => {
    // If a role check is already running, wait for it instead of returning
    if (roleCheckInProgressRef.current && roleCheckPromiseRef.current) {
      const versionBeforeWait = authVersionRef.current;
      await roleCheckPromiseRef.current;
      if (currentUserIdRef.current === userId &&
          (versionBeforeWait !== authVersionRef.current || lastCheckedUserIdRef.current !== userId)) {
        await checkUserRole(userId);
      }
      return;
    }

    roleCheckInProgressRef.current = true;
    setCheckingRole(true);

    const authVersion = authVersionRef.current;
    const doCheck = async () => {
      // Verify user is still authenticated before making network calls
      // This prevents 401 errors when signOut happens between signIn and role check (e.g., 2FA flow)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user || session.user.id !== userId) {
        return;
      }

      // First, try to read role from JWT app_metadata (faster).
      // getUser() validates against the server and acts as our session-validity
      // gate: a 401/403 (e.g. session_not_found) means the stored session was
      // revoked server-side while its JWT still lingers in storage. In that case
      // silently purge it — never resolve a role from a dead session, and never
      // fall through to the anon DB query below (which would hit RLS as anon).
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        if (authVersion !== authVersionRef.current) return;
        setIsAdmin(false);
        setAdminRequires2FA(false);
        const status = (userError as { status?: number }).status;
        const invalidSession =
          status === 401 || status === 403 ||
          /session_not_found|session from session_id/i.test(userError.message ?? '');
        if (invalidSession) {
          // Background cleanup — no navigate / no toast (not a user-initiated logout).
          await supabase.auth.signOut({ scope: 'local' });
          clearSessionToken();
        }
        // On a transient (non-auth) error, leave state as-is and bail without
        // signing the user out — avoids a logout on a connectivity blip.
        return;
      }
      if (!user || authVersion !== authVersionRef.current) {
        return;
      }
      const roleFromJWT = user.app_metadata?.role;
      adminMembershipRef.current = roleFromJWT === "admin";
      setAdminRequires2FA(false);

      if (roleFromJWT === 'admin') {
        const status = await getAdminAuthStatus();
        if (authVersion !== authVersionRef.current) return;
        setIsAdmin(status.is_admin && status.session_verified);
        setAdminRequires2FA(status.is_admin && status.two_factor_enabled && !status.session_verified);
        setIsOwner(false);
        setIsEmployee(false);
        setIsClient(false);
        return;
      }

      if (roleFromJWT === 'owner') {
        setIsAdmin(false);
        setIsOwner(true);
        setIsEmployee(false);
        setIsClient(false);
        return;
      }

      if (roleFromJWT === 'employee') {
        setIsAdmin(false);
        setIsOwner(false);
        setIsEmployee(true);
        setIsClient(false);
        return;
      }

      if (roleFromJWT === 'customer') {
        setIsAdmin(false);
        setIsOwner(false);
        setIsEmployee(false);
        setIsClient(true);
        return;
      }

      // Fallback: direct DB query for users without JWT claims
      // (existing users created before app_metadata update)
      const { data: rolesData } = await supabase
        .from("user_role_memberships")
        .select("role")
        .eq("user_id", userId);

      const roles = (rolesData ?? []).map((r) => r.role);
      adminMembershipRef.current = roles.includes("admin");
      const adminStatus = roles.includes("admin") ? await getAdminAuthStatus() : null;
      if (authVersion !== authVersionRef.current) return;
      setIsAdmin(adminStatus?.is_admin === true && adminStatus.session_verified === true);
      setAdminRequires2FA(adminStatus?.is_admin === true && adminStatus.two_factor_enabled && !adminStatus.session_verified);
      setIsOwner(roles.includes("owner"));
      setIsEmployee(roles.includes("employee"));
      setIsClient(roles.includes("customer"));
    };

    const promise = doCheck().catch((error) => {
      if (authVersion !== authVersionRef.current) return;
      console.error('Error checking user role:', error);
      setIsAdmin(false);
      setAdminRequires2FA(false);
      setIsOwner(false);
      setIsEmployee(false);
      setIsClient(false);
    }).finally(() => {
      roleCheckInProgressRef.current = false;
      if (authVersion === authVersionRef.current || !currentUserIdRef.current) setCheckingRole(false);
      if (authVersion === authVersionRef.current) lastCheckedUserIdRef.current = userId;
      roleCheckPromiseRef.current = null;
    });

    roleCheckPromiseRef.current = promise;
    await promise;
  };

  const signOut = useCallback(async (reason?: SessionInvalidReason, skipBroadcast = false) => {
    // `onClick={signOut}` passe l'ÉVÉNEMENT de clic comme premier argument
    // (OwnerDashboard, MobileHeader, BookingHistory). Un événement ne se clone
    // pas : `postMessage` levait `DataCloneError`, avalée plus bas, et AUCUN
    // autre onglet n'était prévenu. Seule une vraie raison (chaîne) est gardée.
    const safeReason = typeof reason === 'string' ? reason : undefined;

    // Set ref immediately (synchronous) to prevent race conditions with session validation
    isSigningOutRef.current = true;

    // Avant tout `await` : les lectures qui attendent le verrou d'auth et
    // passeront derrière la révocation doivent déjà trouver ce drapeau levé
    // (la porte les retient en silence, spec du 2026-09-27).
    markLogoutStarted();

    // Notify other tabs to also log out, unless we're the one already responding to a broadcast
    if (!skipBroadcast && broadcastChannel) {
      try {
        // `userId` : les autres onglets ne suivent que s'ils affichent la même
        // personne (`crossTabSignOut.ts`). Lu avant la remise à zéro de l'état.
        broadcastChannel.postMessage({ type: 'SIGN_OUT', reason: safeReason, userId: shownUserIdRef.current });
      } catch {
        // Channel may be closed during teardown — safe to ignore
      }
    }

    try {
      try {
        // Set signing out flag to prevent auth page redirect
        setIsSigningOut(true);

        // Show toast explaining why user was logged out
        if (safeReason === 'session_replaced') {
          toast.warning(t("session.invalidatedTitle"), { description: t("session.invalidatedDesc") });
        } else if (safeReason === 'session_expired') {
          toast.warning(t("session.expiredTitle"), { description: t("session.expiredDesc") });
        } else if (safeReason === 'jwt_expired') {
          toast.warning(t("session.jwtExpiredTitle"), { description: t("session.jwtExpiredDesc") });
        }

        // Clear state immediately
        setIsAdmin(false);
        setAdminRequires2FA(false);
        setIsOwner(false);
        setIsEmployee(false);
        setIsClient(false);
        setCheckingRole(false);
        setSession(null);
        setUser(null);
        // La porte suit la session : la déconnexion locale ne doit pas dépendre
        // du réseau. `revokeSessionForLogout` avale les erreurs de `signOut`, et
        // sans événement `SIGNED_OUT` le drapeau resterait armé — sur natif il n'y
        // a pas de rechargement pour le remettre à zéro, et le tunnel `/book`
        // anonyme se mettrait à rendre des 401 jusqu'au redémarrage de l'app.
        markSessionAbsent();
        lastCheckedUserIdRef.current = null;
        setInitialAuthDone(false);

        // Clear session token (single session enforcement)
        clearSessionToken();

        // Clear saved salon from localStorage
        localStorage.removeItem('lastVisitedSalon');

        // Clear owner dashboard state (view and location) but preserve language
        localStorage.removeItem('selectedLocationId');
        localStorage.removeItem('activeView');
        localStorage.removeItem('ownerDashboard_selectedLocation');
        localStorage.removeItem('ownerDashboard_activeView');
        localStorage.removeItem('ownerDashboard_currentDate');

        // Clear direct booking session data
        sessionStorage.removeItem('direct_booking_salon_id');
        sessionStorage.removeItem('direct_booking_active');
        // Annonce in-app : on remet à zéro l'état de l'ouverture (variable de
        // portée module ET marqueur de stockage), pas seulement une clé.
        // Un appareil partagé (tablette d'accueil de salon, un seul onglet ouvert
        // toute la journée) doit repartir à zéro entre deux personnes : sans ça,
        // seule la première personne connectée ce jour-là voit jamais une annonce.
        // ⚠️ Sur NATIF la sortie est une navigation SPA, donc même processus et
        // même instance de module : retirer la clé seule ne suffirait pas.
        resetAnnouncementOpenState();

        // Coordonnées du tunnel : elles survivaient à la déconnexion, si bien que
        // la personne suivante sur le même appareil voyait l'adresse de la
        // précédente à l'écran de confirmation (production, 2026-08-01).
        clearStoredBookingContact();

        // Clear all React Query caches to avoid cross-user data leaks
        queryClient.clear();

        // Revoke the session server-side. A logout now revokes only THIS device's
        // session (scope:'local') so other devices stay logged in; the full local
        // teardown above (state reset, clearSessionToken, queryClient.clear,
        // BroadcastChannel) prevents this device from silently re-establishing the
        // session.
        await revokeSessionForLogout(safeReason, skipBroadcast);

        // Filet natif : `sessionStorage` ne meurt plus à la fermeture de l'app, donc un
        // `signOut` raté laisserait la session rouvrable. On vide le coffre à la main.
        await clearNativeAuthStore();

        // Leave the authenticated app. On web this hard-redirects to /fr (full
        // page reload) so we never deadlock on RedirectToMarketing's spinner
        // (signOut left initialAuthDone=false and nothing resets it without a
        // reload); on native it SPA-navigates to '/' (NativeAppHome).
        leaveToPublicRoute(navigate);

        // Reset signing out flag after navigation
        scheduleDeferred(() => {
          setIsSigningOut(false);
          isSigningOutRef.current = false;
        }, 100);
      } catch (error) {
        console.error('Error during sign out:', error);
        // Even if there's an error, clear local state and navigate
        setIsAdmin(false);
        setAdminRequires2FA(false);
        setIsOwner(false);
        setIsEmployee(false);
        setIsClient(false);
        setCheckingRole(false);
        setSession(null);
        setUser(null);
        // Même raison qu'à la ligne 378 : le drapeau est de l'état de session, il
        // doit suivre le même démontage inconditionnel. Sur natif il n'y a pas de
        // rechargement pour le remettre à zéro ; un drapeau resté armé transforme
        // le tunnel `/book` anonyme en source de 401 synthétiques.
        markSessionAbsent();
        lastCheckedUserIdRef.current = null;
        setInitialAuthDone(false);

        // Clear session token even on error
        clearSessionToken();

        // Même filet que dans la branche succès : surtout ici, où quelque chose a déjà
        // échoué.
        await clearNativeAuthStore();

        localStorage.removeItem('lastVisitedSalon');
        localStorage.removeItem('selectedLocationId');
        localStorage.removeItem('activeView');
        localStorage.removeItem('ownerDashboard_selectedLocation');
        localStorage.removeItem('ownerDashboard_activeView');
        localStorage.removeItem('ownerDashboard_currentDate');

        // Même sur un chemin d'erreur : une déconnexion ratée ne doit pas laisser
        // les coordonnées d'un client à la personne suivante.
        clearStoredBookingContact();

        // Même geste que sur le chemin de succès : l'état de l'annonce ne doit
        // pas non plus survivre à une déconnexion qui a échoué en route.
        resetAnnouncementOpenState();

        // Ensure React Query cache is also cleared on error paths
        queryClient.clear();

        leaveToPublicRoute(navigate);
        scheduleDeferred(() => {
          setIsSigningOut(false);
          isSigningOutRef.current = false;
        }, 100);
      }
    } finally {
      // Le drapeau doit retomber à CHAQUE sortie de `signOut`, même si une étape
      // du chemin d'échec lève à son tour (`clearNativeAuthStore` ou
      // `leaveToPublicRoute` dans le `catch` ci-dessus) : sans ce filet, l'accueil
      // natif resterait bloqué jusqu'à la prochaine connexion.
      // Un `setTimeout` simple, pas `scheduleDeferred` : celui-ci annule ses
      // minuteurs au démontage d'`AuthProvider` (lignes 104-110 plus haut), et
      // le drapeau est un état de MODULE qui survit au fournisseur. Un
      // démontage dans la fenêtre de 100 ms (plantage attrapé par la frontière
      // d'erreur racine) le laisserait donc levé jusqu'à la prochaine
      // connexion. `setTimeout` ne touche ni l'état React ni `window`, la
      // raison d'être de `scheduleDeferred` ne s'applique donc pas ici.
      setTimeout(markLogoutFinished, 100);
    }
  }, [navigate, t, broadcastChannel, scheduleDeferred]);

  // Listen for sign-out messages from other tabs
  useEffect(() => {
    if (!broadcastChannel) return;

    const handleMessage = (e: MessageEvent) => {
      // Un onglet anonyme ou d'un autre compte ignore la déconnexion d'autrui :
      // un propriétaire qui se déconnecte éjectait le client en pleine
      // réservation, et l'inverse fermait le tableau de bord.
      if (e.data?.type === 'SIGN_OUT' && shouldFollowCrossTabSignOut(e.data, shownUserIdRef.current)) {
        // Skip re-broadcast to avoid an infinite loop
        signOut(e.data.reason, true);
      }
    };

    broadcastChannel.addEventListener('message', handleMessage);
    return () => {
      broadcastChannel.removeEventListener('message', handleMessage);
    };
  }, [broadcastChannel, signOut]);

  // Close BroadcastChannel on provider unmount
  useEffect(() => {
    return () => {
      broadcastChannel?.close();
    };
  }, [broadcastChannel]);

  // Renouvellement du jeton lié au cycle de vie de l'app native. Voir
  // `src/lib/auth/nativeAutoRefresh.ts`. Sur le web, c'est un no-op.
  useEffect(() => startNativeAutoRefresh(), []);

  const value = useMemo(() => ({
    user, session, loading, signOut, isAdmin, adminRequires2FA, isOwner, isEmployee, isClient, checkingRole, isSigningOut, initialAuthDone, setAdminState
  }), [user, session, loading, signOut, isAdmin, adminRequires2FA, isOwner, isEmployee, isClient, checkingRole, isSigningOut, initialAuthDone, setAdminState]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};